import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import StatusPill from "../components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

export default function InstallersPage() {
  const [installers, setInstallers] = useState([]);
  const [crews, setCrews] = useState([]);
  const [projects, setProjects] = useState([]);
  const [scopes, setScopes] = useState({ skills: [] });
  const [open, setOpen] = useState(false);
  const [crewOpen, setCrewOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", home_zip_code: "", skills: [], availability_status: "available",
    max_hours_per_day: 8, is_lead: false, lead_project_ids: [], active: true, notes: "",
  });
  const [crewForm, setCrewForm] = useState({ name: "", member_installer_ids: [], notes: "" });

  const load = async () => {
    const [i, c, p, s] = await Promise.all([
      api.get("/installers"), api.get("/crews"), api.get("/projects"), api.get("/scopes"),
    ]);
    setInstallers(i.data || []);
    setCrews(c.data || []);
    setProjects(p.data || []);
    setScopes(s.data || { skills: [] });
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      await api.post("/installers", form);
      toast.success("Installer added");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", home_zip_code: "", skills: [], availability_status: "available", max_hours_per_day: 8, is_lead: false, lead_project_ids: [], active: true, notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete installer?")) return;
    await api.delete(`/installers/${id}`);
    load();
  };
  const toggleSkill = (sk) => {
    setForm((f) => ({ ...f, skills: f.skills.includes(sk) ? f.skills.filter((x) => x !== sk) : [...f.skills, sk] }));
  };
  const toggleLeadProject = (pid) => {
    setForm((f) => ({ ...f, lead_project_ids: f.lead_project_ids.includes(pid) ? f.lead_project_ids.filter((x) => x !== pid) : [...f.lead_project_ids, pid] }));
  };

  const submitCrew = async () => {
    if (!crewForm.name) { toast.error("Crew name required"); return; }
    if (crewForm.member_installer_ids.length < 2) { toast.error("Select at least 2 members"); return; }
    await api.post("/crews", crewForm);
    toast.success("Crew created");
    setCrewOpen(false);
    setCrewForm({ name: "", member_installer_ids: [], notes: "" });
    load();
  };
  const delCrew = async (id) => {
    if (!window.confirm("Delete crew?")) return;
    await api.delete(`/crews/${id}`);
    load();
  };

  const installerById = Object.fromEntries(installers.map((i) => [i.id, i]));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-gray-500 mt-0.5">Manage installers, leads, skills & crews</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={crewOpen} onOpenChange={setCrewOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="new-crew-btn"><Users className="w-4 h-4 mr-1" /> New Crew</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Crew</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input data-testid="crew-name-input" value={crewForm.name} onChange={(e) => setCrewForm({ ...crewForm, name: e.target.value })} /></div>
                <div>
                  <Label>Members</Label>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    {installers.map((i) => (
                      <label key={i.id} className="text-sm flex items-center gap-2 border border-gray-200 rounded-md px-2 py-1 cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={crewForm.member_installer_ids.includes(i.id)}
                          onChange={() => setCrewForm((f) => ({ ...f, member_installer_ids: f.member_installer_ids.includes(i.id) ? f.member_installer_ids.filter((x) => x !== i.id) : [...f.member_installer_ids, i.id] }))}
                        />
                        {i.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea rows={2} value={crewForm.notes} onChange={(e) => setCrewForm({ ...crewForm, notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={submitCrew} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="crew-submit-btn">Create Crew</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-installer-btn"><Plus className="w-4 h-4 mr-1" /> New Installer</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>New Installer</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name *</Label><Input data-testid="installer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Home ZIP</Label><Input value={form.home_zip_code} onChange={(e) => setForm({ ...form, home_zip_code: e.target.value })} /></div>
                <div><Label>Max hours/day</Label><Input type="number" step="0.5" value={form.max_hours_per_day} onChange={(e) => setForm({ ...form, max_hours_per_day: Number(e.target.value) })} /></div>
                <div>
                  <Label>Availability</Label>
                  <select className="h-9 w-full text-sm border border-gray-300 rounded-md px-2 bg-white" value={form.availability_status} onChange={(e) => setForm({ ...form, availability_status: e.target.value })}>
                    <option value="available">Available</option>
                    <option value="pto">PTO</option>
                    <option value="busy">Busy</option>
                  </select>
                </div>
                <div className="col-span-2 flex items-center gap-3">
                  <div className="flex items-center gap-2"><Switch checked={form.is_lead} onCheckedChange={(v) => setForm({ ...form, is_lead: v })} data-testid="installer-lead-switch" /><Label>Lead Installer</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
                </div>
                <div className="col-span-2">
                  <Label>Skills</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(scopes.skills || []).map((sk) => (
                      <button type="button" key={sk} onClick={() => toggleSkill(sk)} className={`text-xs px-2 py-1 rounded-full border ${form.skills.includes(sk) ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-300 text-gray-700"}`}>
                        {sk}
                      </button>
                    ))}
                  </div>
                </div>
                {form.is_lead && (
                  <div className="col-span-2">
                    <Label>Lead on jobs</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {projects.map((p) => (
                        <button type="button" key={p.id} onClick={() => toggleLeadProject(p.id)} className={`text-xs px-2 py-1 rounded-full border ${form.lead_project_ids.includes(p.id) ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-white border-gray-300 text-gray-700"}`}>
                          {p.project_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="installer-submit-btn">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {crews.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-4 overflow-hidden">
          <header className="px-3 py-2 border-b border-gray-200 bg-gray-50/50">
            <h3 className="text-sm font-semibold text-gray-900">Crews</h3>
            <p className="text-xs text-gray-500">Crews are auto-dispatched together</p>
          </header>
          <div className="divide-y divide-gray-100">
            {crews.map((c) => (
              <div key={c.id} className="px-3 py-2 flex items-center gap-3 text-sm">
                <span className="font-medium text-gray-900">{c.name}</span>
                <span className="text-xs text-gray-600">{(c.member_installer_ids || []).map((id) => installerById[id]?.name).filter(Boolean).join(" + ")}</span>
                <Button variant="ghost" size="sm" onClick={() => delCrew(c.id)} className="ml-auto"><Trash2 className="w-3.5 h-3.5 text-gray-500" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-xs text-gray-500 uppercase tracking-wider">
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">Phone</th>
              <th className="text-left px-3 py-2 font-medium">ZIP</th>
              <th className="text-left px-3 py-2 font-medium">Skills</th>
              <th className="text-left px-3 py-2 font-medium">Crew</th>
              <th className="text-left px-3 py-2 font-medium">Hrs/Day</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {installers.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-12 text-center text-gray-500">No installers yet.</td></tr>
            ) : installers.map((i) => {
              const myCrew = crews.find((c) => c.id === i.crew_id);
              return (
                <tr key={i.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{i.name}</span>
                      {i.is_lead && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5">Lead</span>}
                    </div>
                    {i.email ? (
                      <a
                        href={`mailto:${i.email}`}
                        className="text-xs text-blue-600 hover:underline"
                        data-testid={`installer-email-${i.id}`}
                      >
                        {i.email}
                      </a>
                    ) : (
                      <div className="text-xs text-gray-500">—</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {i.phone ? (
                      <a href={`tel:${String(i.phone).replace(/\D/g, "")}`} className="text-blue-600 hover:underline">
                        {(() => {
                          const d = String(i.phone).replace(/\D/g, "");
                          if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                          if (d.length === 11 && d[0] === "1") return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
                          return i.phone;
                        })()}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{i.home_zip_code || "—"}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs max-w-[260px] truncate">{(i.skills || []).join(", ") || "—"}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{myCrew?.name || "—"}</td>
                  <td className="px-3 py-2 text-gray-700">{i.max_hours_per_day}</td>
                  <td className="px-3 py-2"><StatusPill value={i.active ? "active" : "cancelled"} /></td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => del(i.id)} data-testid={`installer-delete-${i.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-gray-500" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
